import React, { useState, useEffect } from "react";

import { Alert } from "services/Alert";
import { connect } from "react-redux";

import {
    BLOCKED_DESTINATIONS_CREATE_API, BLOCKED_DESTINATIONS_MODIFY_API,
    BLOCKED_DESTINATIONS_REMOVE_API, BLOCKED_DESTINATIONS_MODIFY_LIST_API,
    SMS_BLOCKED_DESTINATIONS_CREATE_API, SMS_BLOCKED_DESTINATIONS_MODIFY_LIST_API,
    SMS_BLOCKED_DESTINATIONS_REMOVE_API
} from "const/apiMethods";
import { checkPermissionsFor } from "store/storeHelpers";
import { fieldSorter, servicePick } from "utils";
import {
    getBlockedDestinationsList,
    onAddBlockDestinationsItem,
    onRemoveBlockDestinationsItem,
    onEditBlockDestinationsItemList
} from "actions/blocked_destinations";
import PanelLayout from "components/base/PanelLayout";
import PageHeader from "components/base/PageHeader";
import BlockedDestinationsFilters from "pages/BlockedDestinations/BlockedDestinationsFilters";
import BlockedDestinationsTable from "pages/BlockedDestinations/BlockedDestinationsTable";

import { FlexboxGrid } from "rsuite";

const searchedData = ["sde_wz_name", "sde_de_name", "sde_name", "prefix", "sp_name", "acc_name", "action"];


const BlockedDestinationsPage = ({
    data,
    loading,
    service,
    accountList,
    reportLoading,
    servicePlanList,
    worldzoneList,
    destinationList,
    subdestinationList,
    getBlockedDestinationsList,

    createApiFunc,
    modifyApiFunc,
    removeApiFunc
}) => {
    const [tableData, setTableData] = useState([]);
    const [filters, setFilters] = useState({});

    useEffect(() => {
        getBlockedDestinationsList(filters, service);
    }, []);

    useEffect(() => {
        const tableDataList = data.filter((item) => {
            // return item if filter is empty
            if (!filters || filters && !filters.str)
                return true;

            const match = filters.str.toLowerCase();

            const filterMatching = searchedData.map((keyName) => {
                if (item[keyName]) {
                    const matchedString = item[keyName].toString().toLowerCase();

                    if (matchedString.includes(match)) {
                        return true;
                    }
                }
            });

            return filterMatching.some(dataMatch => !!dataMatch);
        });

        tableDataList.sort(fieldSorter(["wz_key", "de_key", "sde_key", "prefix", "acc_name", "sp_key"]))
        const groupedData = Object.values(tableDataList.reduce((a, curr) => {
            const { acc_id, sde_key, prefix, de_key, wz_key } = curr;
            const key = `${acc_id}_${sde_key}_${prefix}_${de_key}_${wz_key}`;
            (a[key] = a[key] || []).push(curr);
            return a;
        }, {}));

        const combinedNumber = groupedData.map((value) => {
            const duplicateList = value.map(duplicate => {
                return { sp_key: duplicate.sp_key, sp_name: duplicate.sp_name, ab_key: duplicate.ab_key };
            });

            return { ...value[0], sp_duplicates: duplicateList, sp_key_list: duplicateList.map(item => item.sp_key) }
        });
        setTableData(combinedNumber);
    }, [data, filters]);

    const update = () => {
        getBlockedDestinationsList(filters, service);
    };

    const onChangeFilters = (filterValue) => {
        setFilters(filterValue);
        getBlockedDestinationsList(filterValue, service);
    };

    const handleCreate = async (params) => {
        try {
            const { sp_key_list: spKeyList = [], ...restParams } = params ?? {};

            const base = {
                ...restParams,
                wz_key: params?.wz_key ? params.wz_key : null,
                de_key: params?.de_key ? params.de_key : null,
                sde_key: params?.sde_key ? params.sde_key : null,
                account_id: params?.account_id ? params.account_id : null,
            };
            const keys = spKeyList.length ? spKeyList : [null];

            const currentData = tableData.find(item => base.account_id === item.acc_id) || {};
            const duplicateKeys = currentData?.sp_duplicates?.map(item => item.ab_key) || [];
            const filteredData = data.filter(item => duplicateKeys.includes(item.ab_key));
            const anyRow = filteredData.find(item => item.sp_key === null);

            if (params.sp_key_list && params.sp_key_list.filter(spKey => spKey === null).length) {
                const toDeleteAbKeyList = filteredData.map(item => item.ab_key);

                try {
                    await createApiFunc({ ...base, sp_key: null }, service);

                    const responses = await Promise.all(toDeleteAbKeyList.map((key) => {
                        return removeApiFunc(key, service).then(() => {
                            const deletedItem = data.find(item => item.ab_key === key);
                            Alert.info(`${deletedItem.sp_name} has been deleted`);
                        });
                    }));

                    return responses;
                } catch (error) {
                    // throw error;
                }
            } else {
                if (anyRow) {
                    await removeItemAsync([anyRow]);
                }

                const responses = await Promise.all(
                    keys.map(sp_key => {
                        return createApiFunc({ ...base, sp_key: sp_key }, service);
                    })
                );

                if (responses.length && responses.every(response => !response.error)) {
                    Alert.success(`Blocked destination created! (${responses.length})`);
                }

                return responses;
            }
        } catch (error) {
            throw error;
        }
    };

    const handleModify = async (params, rowData) => {
        const duplicateKeys = rowData.sp_duplicates.map(item => item.ab_key);
        const filteredData = data.filter(item => duplicateKeys.includes(item.ab_key));

        if (!params.sp_key_list.length) {
            const toDeleteAbKeyList = filteredData.map(item => item.ab_key);

            try {
                toDeleteAbKeyList.forEach(async (key) => {
                    await removeApiFunc(key, service).then(() => {
                        const deletedItem = data.find(item => item.ab_key === key);
                        Alert.info(`${deletedItem.sp_name} has been deleted`);
                    })
                });
            } catch (error) {
                // throw error;
            }
        } else {
            const absentRows = filteredData.filter(item => !params.sp_key_list.includes(item.sp_key));
            const existRows = filteredData.filter(item => params.sp_key_list.includes(item.sp_key));

            const defaultData = {
                action: rowData?.action,
                wz_key: rowData?.wz_key,
                de_key: rowData?.de_key,
                sde_key: rowData?.sde_key,
                prefix: rowData?.prefix
            };

            const paramsWithoutSpKeyList = Object.assign(defaultData, params, {
                sp_key: null,
                sp_key_list: null,
                account_id: params?.account_id === undefined ? rowData?.acc_id : params.account_id
            });
            const spKeyList = params.sp_key_list;

            try {
                const response = await modifyApiFunc(duplicateKeys, paramsWithoutSpKeyList, service);

                await addItemAsync(spKeyList, existRows, paramsWithoutSpKeyList, filteredData);
                await removeItemAsync(absentRows);

                if (response && !response.error) {
                    Alert.success(`${duplicateKeys.length} rows were updated`);
                }

                return response;
            } catch (error) {
                throw error;
            }
        }
    };

    const handleRemove = async (key) => {
        try {

            const tableRow = tableData.find(value => value.ab_key === key);
            const duplicateKeys = tableRow.sp_duplicates.map(duplicate => duplicate.ab_key);

            const responses = await Promise.all(
                duplicateKeys.map(key =>
                    removeApiFunc(key, service)
                )
            );

            if (responses.length && responses.every(response => !response.error)) {
                Alert.success(`Blocked destinations removed! (${duplicateKeys.length})`);
            }

            return responses;
        } catch (error) {
            throw error;
        }
    };

    const addItemAsync = async (spKeyList, existRows, params, filteredData) => {
        const diffSpKeyList = spKeyList.filter(sp_key => !existRows.map(row => row.sp_key).includes(sp_key))

        for (const spKey of diffSpKeyList) {
            let dataToEdit = {};

            if (filteredData.length) {
                dataToEdit = params;
            }

            await createApiFunc({ ...dataToEdit, sp_key: spKey }, service);
        }
    };

    const removeItemAsync = async (absentRows) => {
        if (absentRows.length) {
            for (const item of absentRows) {
                const deletedItem = data.find(delItem => delItem.ab_key === item.ab_key);

                await removeApiFunc(deletedItem.ab_key, service);
            }
        }
    };

    return (
        <PanelLayout>
            <PageHeader title="Blocked Destinations" />
            <FlexboxGrid>
                <FlexboxGrid.Item colspan={24}>
                    <BlockedDestinationsFilters onSubmit={onChangeFilters} defaultFilters={filters} />
                </FlexboxGrid.Item>
            </FlexboxGrid>
            <BlockedDestinationsTable
                data={tableData}

                onDataUpdate={update}

                onRowCreate={handleCreate}
                onRowSubmit={handleModify}
                onRowRemove={handleRemove}

                createPermission={checkPermissionsFor(servicePick(service, BLOCKED_DESTINATIONS_CREATE_API, SMS_BLOCKED_DESTINATIONS_CREATE_API))}
                modifyPermission={checkPermissionsFor(servicePick(service, BLOCKED_DESTINATIONS_MODIFY_LIST_API, SMS_BLOCKED_DESTINATIONS_MODIFY_LIST_API))}
                removePermission={checkPermissionsFor(servicePick(service, BLOCKED_DESTINATIONS_REMOVE_API, SMS_BLOCKED_DESTINATIONS_REMOVE_API))}

                {...{
                    filter: filters,
                    service,
                    loading,
                    reportLoading,
                    accountList,
                    servicePlanList,
                    worldzoneList,
                    destinationList,
                    subdestinationList,
                }}
            />
        </PanelLayout>
    );
};

const mapStateToProps = ({ auth, blocked_destinations, references }) => ({
    data: blocked_destinations.list,
    worldzoneList: references.worldzone_list,
    destinationList: references.destination_list,
    subdestinationList: references.subdestination_list,
    servicePlanList: references.service_plan_list,
    accountList: auth.service ? references.dialer_list : references["sms.dialer_list"],
    reportLoading: references.reportLoading,
    loading: blocked_destinations.loading,
    service: auth.service
});

const mapDispatchToProps = {
    getBlockedDestinationsList,
    createApiFunc: onAddBlockDestinationsItem,
    modifyApiFunc: onEditBlockDestinationsItemList,
    removeApiFunc: onRemoveBlockDestinationsItem
};

export default connect(mapStateToProps, mapDispatchToProps)(BlockedDestinationsPage);